import { RetailerClientState } from '@state/reducers/retailer-client/RetailerClientReducer';
import {
  Country,
  CountryFilterPair,
  FactRevenue,
  FeatureOption,
  Filters,
  Option,
  PageName,
  PageType,
  ShareType,
  SubSection,
} from '@utils/pageConfig/Types';
import { ProductType } from '@pages/product-analyzer/Product';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { GrowthTypes } from '@ecosystem/formatters';
import StackedChart from '@data-visualization/stacked-chart/StackedChart';
import { FilterCriteriaParam } from '@models/CommonParam';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { getDisplayNameFromHeaderKey } from '@models/PerformanceAnalysisboard';
import { useToastMessage } from '@components/toast-message/ToastMessage';
import { useChartExport } from '@ecosystem/export-utils';
import {
  dataExportedTrackingFunction,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { useExportStackedChart } from '@hooks/export/export-stacked-chart/UseExportStackedChart';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FeatureSelectorType } from '@components/feature-selector/Types';
import { ViewType } from '@hooks/export/Model';
import { EXPLORE_SEGMENTS_CARD_TITLE, EXPLORE_STACK_BAR_CHART_TITLE } from '@pages/Utilities';
import { getPeriodicityForMATAndYTD } from '@utils/PeriodUtils';
import getComparisonPeriod from '@components/filter-selection-note/GetComparisonPeriod';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import { modifiedPAStackChartGrandTotalToTotalValue } from '@utils/TotalUtils';
import { SortingOptionsType } from '@components/design/migrated/Table';
import { getExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { displayExportErrorToastMessage, getAppliedFiltersText } from '@hooks/export/ExportUtils';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { PerformanceAnalysisStackedBarChartInsight } from '@components/collections/performance-analysis-stacked-bar-chart/Model';
import { CollectionDataType } from '@ecosystem/newron-client-collections/build/shared/types';
import { filterByAccessibleCountries } from '@utils/collectionUtils/CollectionUtils';
import { containers } from '@ecosystem/newron-design-system';
import { checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries } from '@utils/multiCountryUtils/MultiCountryUtils';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { filterAppliedFiltersForStackedChartRequest } from '@utils/exploreUtils/ExploreUtils';
import { useGetStackedChartData } from '@utils/hook/explore/UseGetStackedChartData';
import MarketPerformancePosFactsTiles from '@components/market-performance-pos-facts-tiles/MarketPerformancePosFactsTiles';
import { SalesPerformance } from '@models/SalesPerformance';
import { useGetStackedChartSegmentFilters } from '@utils/hook/explore/UseGetStackedChartSegmentFilters';
import { cloneDeep } from 'lodash';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { ExploreViewState } from '@models/SavedViews';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { convertToExploreTrendsStack } from '@data-visualization/stacked-chart/StackedChartUtils';
import { exportChartKey } from '@data-visualization/trends-stack-chart/StackedChartUtilities';
import useLimitProductGroup from '@utils/hook/explore/UseLimitProductGroup';

export interface ExploreSegmentProps {
  //Retailer State
  retailerClientState: RetailerClientState;

  //General
  allFeatureValues: Filters[];
  productType: ProductType;
  pgsByAppliedCountries: CountryFilterPair[];
  isRestoreFromSavedView: boolean;
  setIsRestoreFromSavedView: (isRestore: boolean) => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  currentSortingOption: SortingOptions;
  setCurrentSortingOption: Dispatch<SetStateAction<SortingOptions>>;

  //Side filters
  allFilters: Filters[];
  appliedCountries: Option[];
  countriesToQuery: Country[];
  appliedProductGroups: Option[];
  selectedProductGroups?: Option[];

  //Main Filters
  period: BaseTimePeriod;
  periodType: Periodicity;
  comparisonPeriod: BaseTimePeriod | RelativePeriod;

  //Chart filters
  showPerformanceWithin: FeatureOption;
  setShowPerformanceWithin: (feature: FeatureOption) => void;
  selectedShareType: ShareType;
  selectedGrowthType: GrowthTypes;

  //Display
  volume: string;
  factRevenue: FactRevenue;
  revenuePerformance: SalesPerformance;
  volumePerformance: SalesPerformance;
  loadingPosFacts: boolean;
  exploreViewState: ExploreViewState;
  segmentByFeatureParent: FeatureOption;
  setSegmentByFeatureParent: (feature: FeatureOption) => void;
  segmentKpiParent: any;
  isApiError?: boolean;
}

const Segments: React.FC<ExploreSegmentProps> = ({
  retailerClientState,
  allFeatureValues,
  productType,
  allFilters,
  appliedCountries,
  appliedProductGroups,
  selectedProductGroups,
  currentSortingOption,
  setCurrentSortingOption,
  period,
  periodType,
  comparisonPeriod,
  showPerformanceWithin,
  setShowPerformanceWithin,
  selectedShareType,
  pgsByAppliedCountries,
  selectedGrowthType,
  countriesToQuery,
  factRevenue,
  volume,
  revenuePerformance,
  volumePerformance,
  loadingPosFacts,
  isRestoreFromSavedView,
  setIsRestoreFromSavedView,
  setIsLoading,
  exploreViewState,
  segmentByFeatureParent,
  setSegmentByFeatureParent,
  segmentKpiParent,
  isApiError,
}) => {
  const { Card } = containers;

  //Local State
  const [isExportButtonDisabled, setIsExportButtonDisabled] = useState<boolean>(false);

  //Redux State
  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);

  //Custom Hooks

  const pagesConfig = usePageConfig();
  const { download: exportStackedChart } = useExportStackedChart();
  const [invokeExport, onChartExportReady] = useChartExport();
  const { exportImages } = useExportImages(PageType.EXPLORE);
  const handleDataExportTrackingFunction = useDataExportTracking('mainFilters');
  const { showToastMessage } = useToastMessage();
  const {
    filteredShowPerformanceWithinFeatureList,
    filteredSegmentByFeatureList,
    segmentByFeature,
    setSegmentByFeature,
  } = useGetStackedChartSegmentFilters({
    allFeatureValues,
    allFilters,
    productType,
    countriesToQuery,
    showPerformanceWithin,
    setShowPerformanceWithin,
    appliedProductGroups,
    selectedProductGroups,
    isRestoreFromSavedView,
    segmentByFeature: segmentByFeatureParent,
    setSegmentByFeature: setSegmentByFeatureParent,
  });

  useEffect(() => {
    if (segmentByFeatureParent?.value !== segmentByFeature?.value && segmentByFeature?.value) {
      setSegmentByFeatureParent(segmentByFeature);
    }
  }, [segmentByFeature?.value]);

  const isLimitPG = useLimitProductGroup(appliedProductGroups.length, showPerformanceWithin, segmentByFeature);
  const kpi = segmentKpiParent;

  const filteredAppliedFilters = filterAppliedFiltersForStackedChartRequest(allFilters, period, periodType);

  const getStackChartParams: FilterCriteriaParam = cloneDeep({
    clientId: retailerClientState.clientId,
    productType,
    countries: countriesToQuery,
    productGroup: appliedProductGroups,
    period: period.value,
    comparisonPeriod: comparisonPeriod,
    periodType,
    shareType: selectedShareType,
    appliedFilters: filteredAppliedFilters,
    segmentName: showPerformanceWithin.feature,
    segmentDataType: showPerformanceWithin.dataType,
    kpi: kpi?.value,
    segmentBy: segmentByFeature.feature,
    segmentByDataType: segmentByFeature.dataType,
    pageName: pagesConfig.pageName,
  });

  const { data, isLoading, isError } = useGetStackedChartData(
    getStackChartParams,
    pagesConfig,
    allFeatureValues,
    isLimitPG,
  );

  const createViewState = useCallback(() => exploreViewState, [exploreViewState]);
  /**
   * Used to show warning message (e.g. loaded product groups banner) only once the stack chart
   * has loaded.
   */
  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading]);

  const noResultHandler = useCallback(() => {
    setIsExportButtonDisabled(true);
  }, [data]);

  const onChartDownload = async (exportFormats: ExportFormat[]): Promise<void> => {
    if (!data || data.length <= 0) {
      return;
    }

    const segmentsChartData = convertToExploreTrendsStack({
      StackChartData: data,
      selectedKpi: kpi?.headerKey,
      selectedGrowthType,
    });

    const exportImage =
      exportImages[exportChartKey(segmentsChartData, ShareType.MarketShare, kpi?.headerKey, true, true)];
    const timePeriod = period.label;
    const finalPeriodType = getPeriodicityForMATAndYTD(period, periodType).periodicity;
    const comparisonTimePeriod = getComparisonPeriod(period, periodType, comparisonPeriod);

    const requestPayload = {
      clientId: retailerClientState.clientId,
      productGroup: appliedProductGroups,
      period: period.value,
      comparisonPeriod: comparisonPeriod,
      periodType,
      segment: showPerformanceWithin.feature,
    };

    const modifiedStackChartKpi = modifiedPAStackChartGrandTotalToTotalValue(data);

    try {
      await exportStackedChart({
        exportFormats,
        invokeExport,
        title: EXPLORE_SEGMENTS_CARD_TITLE,
        appliedCountry: appliedCountries,
        appliedPg: appliedProductGroups,
        data: modifiedStackChartKpi,
        timePeriod,
        comparisonTimePeriod,
        factRevenue: factRevenue,
        periodType: finalPeriodType,
        showPerformanceWithin: showPerformanceWithin.value,
        appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.EXPLORE),
        selectedKpi: kpi,
        segmentBy: segmentByFeature.label,
        pageName: PageName.EXPLORE,
        volume: volume,
        exportImage,
      });
      handleExportedTracking(
        ElementType.STACKED_CHART,
        exportFormats,
        `${getDisplayNameFromHeaderKey(pagesConfig.sortingOptionsPerformanceAnalysisStackedChart.headerKey, volume)}`,
      );
    } catch (err: any) {
      displayExportErrorToastMessage(
        err,
        'Explore Segments - exportStackedChart error.',
        requestPayload,
        showToastMessage,
      );
      return;
    }
  };

  useEffect(() => {
    setIsExportButtonDisabled(isLoading || isError); //NOSONAR typescript:S6606
  }, [isLoading, isError]);

  const isPeriodNotExist = !checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries(
    pagesConfig.appliedFilters,
    mainFiltersState,
    initialFiltersState,
    PageType.EXPLORE,
  );

  const doNothing = () => {
    //This is intentional, as Explore Segments and Performance Analysis
    // sharing same Stacked Chart Component and some props no longer needed
  };

  const constructFeatureSelectorType: FeatureSelectorType = {
    isDisabled: isLoading,
    feature: showPerformanceWithin,
    setFeature: setShowPerformanceWithin,
    featureFilters: filteredShowPerformanceWithinFeatureList,
  };

  const createStackedChartInsightDataFn: CreateInsightDataFn<PerformanceAnalysisStackedBarChartInsight> = (
    collectionDataType: CollectionDataType = 'live',
  ) => {
    const { periodType: collectionPeriodType, productGroup = [], countries: collectionCountries } = getStackChartParams;
    const unfilteredAppliedFilters = pagesConfig.appliedFilters[PageType.EXPLORE].selectedFilters;

    const filteredApiParams = filterByAccessibleCountries(
      unfilteredAppliedFilters,
      pgsByAppliedCountries,
      collectionPeriodType,
      collectionCountries,
      productGroup,
    );
    return {
      apiParams: {
        ...getStackChartParams,
        ...filteredApiParams,
      },
      insightCardTitle: EXPLORE_SEGMENTS_CARD_TITLE,
      segmentByFeature: segmentByFeature,
      showPerformanceWithin: showPerformanceWithin,
      selectedGrowthType: selectedGrowthType,
      sortByStackedChart: pagesConfig.sortByOptionsPerformanceAnalysisStackedChart,
      currencySymbol: factRevenue.currencySymbol,
      volume: volume,
      collectionDataType: collectionDataType,
      retailerClientName: retailerClientState.clientName!,
      createViewStateFn: createViewState,
    };
  };

  const handleExportedTracking = (
    elementType: ElementType,
    exportFormats: ExportFormat[],
    elementKpi?: string,
    elementSort?: SortingOptionsType,
  ) => {
    const dataExportedTrackingParam: DataExportTrackingElementParam = {
      elementType,
      elementKpi,
      elementSort,
      optionChosen: exportFormats.map((format) => getExportTrackingFormat(format)),
    };
    handleDataExportTrackingFunction(dataExportedTrackingParam, dataExportedTrackingFunction);
  };

  return (
    <div className="pt-6">
      <MarketPerformancePosFactsTiles
        revenuePerformance={revenuePerformance}
        volumePerformance={volumePerformance}
        selectedGrowthType={selectedGrowthType}
        isLoading={loadingPosFacts}
        factRevenue={factRevenue}
        volume={volume}
        subSectionForAmplitudeTracking={SubSection.EXPLORE_SEGMENTS}
        isApiError={isApiError}
      />
      <br />
      <Card>
        <StackedChart
          stackedChartRespData={data}
          showPerformanceWithin={showPerformanceWithin}
          isLoading={isLoading}
          selectedGrowthType={selectedGrowthType}
          segmentByFeature={segmentByFeature}
          setSegmentByFeature={setSegmentByFeature}
          featureFilters={filteredSegmentByFeatureList}
          factRevenue={factRevenue}
          featureSelectorType={constructFeatureSelectorType}
          currentSortingOption={currentSortingOption}
          setCurrentSortingOption={setCurrentSortingOption}
          featureHandler={doNothing}
          segmentByHandler={doNothing}
          onExportReady={onChartExportReady}
          noResultHandler={noResultHandler}
          volume={volume}
          view={ViewType.StackedChart}
          isRestoreFromSavedView={isRestoreFromSavedView}
          setIsRestoreFromSavedView={setIsRestoreFromSavedView}
          sortByStackedChart={pagesConfig.sortByOptionsPerformanceAnalysisStackedChart}
          setSortByStackedChart={pagesConfig.setSortByOptionsPerformanceAnalysisStackedChart}
          isExplorePage={true}
          createStackedChartInsightDataFn={createStackedChartInsightDataFn}
          isExportButtonDisabled={isExportButtonDisabled}
          setIsExportButtonDisabled={setIsExportButtonDisabled}
          onChartDownload={onChartDownload}
          noPeriodExistForMulticountry={isPeriodNotExist}
          isError={isError}
          isLimitPG={isLimitPG}
          componentName={EXPLORE_STACK_BAR_CHART_TITLE}
          subSectionForAmplitudeTracking={SubSection.EXPLORE_SEGMENTS}
          createViewStateFn={createViewState}
          selectedKpi={kpi?.headerKey}
        />
      </Card>
    </div>
  );
};

export default Segments;
